import React, { useEffect, useState } from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { getFormattedAmount, getFormattedPercentage } from "Shared/Utils";
import Title from "antd/lib/typography/Title";
import { Button } from "antd";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { PerformanceTag } from "Shared/PerformanceTag";

const OverviewCard = (props) => {
    const {
        itemInfo = {
            name: "",
            symbol: "",
            uid: "",
            avatar: "",
            handleMoreClk: () => {},
        },
        itemData = {
            currentValue: 0,
            totalInvestment: 0,
            avgPrice: 0,
            pl: 0,
        },
        currency = "INR",
    } = props;

    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        setImgError(false);
    }, [itemInfo.avatar]);

    const currSymbol = getCurrencySymbol(currency);
    const positiveColor = Tokens.positive || Tokens.profit;
    const negativeColor = Tokens.negative || Tokens.loss;

    const plNumeric = Number(itemData.pl) || 0;
    let plColor = Tokens.textSecondary;
    if (plNumeric > 0) plColor = positiveColor;
    if (plNumeric < 0) plColor = negativeColor;

    const isAssetCard = Boolean(itemData.isAssetCard || itemInfo.symbol);

    const handleCardClick = () => {
        if (typeof itemInfo.handleMoreClk === "function") {
            itemInfo.handleMoreClk(itemInfo.uid);
        }
    };

    if (isAssetCard) {
        const symbolUpper = (itemInfo.symbol || "").toUpperCase();
        const fallbackTicker = (symbolUpper || itemInfo.name || "?")
            .slice(0, 3)
            .toUpperCase();
        const isExited = Boolean(itemData.isExited);

        const plValue =
            itemData.plValue !== undefined
                ? Number(itemData.plValue) || 0
                : (Number(itemData.currentValue) || 0) -
                  (Number(itemData.totalInvestment) || 0);
        let plValueColor = Tokens.textSecondary;
        if (plValue > 0) plValueColor = positiveColor;
        if (plValue < 0) plValueColor = negativeColor;

        const realizedPL = Number(itemData.realizedPL) || 0;
        let realizedColor = Tokens.textSecondary;
        if (realizedPL > 0) realizedColor = positiveColor;
        if (realizedPL < 0) realizedColor = negativeColor;

        const rawShare = Number(itemData.investmentPie) || 0;
        const sharePct =
            rawShare > 0 && rawShare <= 1
                ? Math.round(rawShare * 100)
                : Math.round(rawShare);

        return (
            <div key={itemInfo.uid} style={{ marginBottom: "12px" }}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleCardClick}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCardClick();
                        }
                    }}
                    style={{
                        padding: "16px 20px",
                        borderRadius: Tokens.radiusCard,
                        border: `1px solid ${Tokens.hairline}`,
                        backgroundColor: Tokens.card,
                        cursor: "pointer",
                        opacity: isExited ? 0.88 : 1,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                minWidth: 0,
                            }}
                        >
                            {itemInfo.avatar && !imgError ? (
                                <img
                                    src={itemInfo.avatar}
                                    alt={itemInfo.name || symbolUpper}
                                    onError={() => setImgError(true)}
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: Tokens.raised,
                                        objectFit: "cover",
                                        flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        backgroundColor: Tokens.raised,
                                        border: `1px solid ${Tokens.hairline}`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: Tokens.textSecondary,
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        fontFamily: Tokens.fontMono,
                                        flexShrink: 0,
                                    }}
                                >
                                    {fallbackTicker}
                                </div>
                            )}

                            <div style={{ minWidth: 0 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "15px",
                                            fontWeight: 500,
                                            lineHeight: "1.3",
                                        }}
                                    >
                                        {itemInfo.name}
                                    </span>
                                    {symbolUpper && (
                                        <span
                                            style={{
                                                backgroundColor: Tokens.raised,
                                                color: Tokens.textSecondary,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius: Tokens.radiusTag,
                                                padding: "1px 6px",
                                                fontSize: "11px",
                                                fontFamily: Tokens.fontMono,
                                                fontWeight: 500,
                                                lineHeight: "1.3",
                                            }}
                                        >
                                            {symbolUpper}
                                        </span>
                                    )}
                                    {isExited && (
                                        <span
                                            style={{
                                                backgroundColor: Tokens.raised,
                                                color: Tokens.textSecondary,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius: Tokens.radiusTag,
                                                padding: "1px 6px",
                                                fontSize: "11px",
                                                fontFamily: Tokens.fontMono,
                                                fontWeight: 500,
                                                lineHeight: "1.3",
                                            }}
                                        >
                                            Exited
                                        </span>
                                    )}
                                </div>

                                <div
                                    style={{
                                        color: Tokens.textMuted,
                                        fontSize: "12px",
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        marginTop: "3px",
                                        whiteSpace: isExited
                                            ? "nowrap"
                                            : "normal",
                                    }}
                                >
                                    {isExited
                                        ? `Closed position · ${
                                              itemData.tradeCount || 0
                                          } ${
                                              Number(itemData.tradeCount) === 1
                                                  ? "trade"
                                                  : "trades"
                                          }`
                                        : `${getFormattedAmount(
                                              itemData.quantity || 0,
                                              true,
                                              4
                                          )}${
                                              symbolUpper
                                                  ? ` ${symbolUpper}`
                                                  : ""
                                          } · ${currSymbol}${getFormattedAmount(
                                              itemData.currentPrice || 0,
                                              true,
                                              2
                                          )}${
                                              sharePct > 0
                                                  ? ` · Share: ${sharePct}%`
                                                  : ""
                                          }`}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: "4px",
                                flexShrink: 0,
                            }}
                        >
                            {isExited ? (
                                <React.Fragment>
                                    <div
                                        style={{
                                            color: realizedColor,
                                            fontSize: "15px",
                                            fontWeight: 600,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {`Realized ${
                                            realizedPL >= 0 ? "+" : "-"
                                        }${currSymbol}${getFormattedAmount(
                                            Math.abs(realizedPL),
                                            true,
                                            2
                                        )}`}
                                    </div>
                                    <div>
                                        {PerformanceTag(
                                            itemData.realizedRoiPct || 0,
                                            true
                                        )}
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            itemData.currentValue || 0,
                                            true,
                                            2
                                        )}
                                    </div>
                                    <div>
                                        {PerformanceTag(itemData.pl, true)}
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            borderTop: `1px solid ${Tokens.hairline}`,
                            paddingTop: "10px",
                            marginTop: "12px",
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "8px",
                        }}
                    >
                        {isExited ? (
                            <React.Fragment>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Total bought
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            itemData.lifetimeInvested || 0,
                                            true,
                                            2
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Total sold
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            itemData.totalSold || 0,
                                            true,
                                            2
                                        )}
                                    </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Avg exit price
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {itemData.avgExitPrice > 0
                                            ? `${currSymbol}${getFormattedAmount(
                                                  itemData.avgExitPrice,
                                                  true,
                                                  2
                                              )}`
                                            : "—"}
                                    </div>
                                </div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Invested
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            itemData.totalInvestment || 0,
                                            true,
                                            2
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Avg buy price
                                    </div>
                                    <div
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {itemData.avgPrice > 0
                                            ? `${currSymbol}${getFormattedAmount(
                                                  itemData.avgPrice,
                                                  true,
                                                  2
                                              )}`
                                            : "—"}
                                    </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div
                                        style={{
                                            color: Tokens.textMuted,
                                            fontSize: "11px",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        Unrealized P&amp;L
                                    </div>
                                    <div
                                        style={{
                                            color: plValueColor,
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {plValue >= 0 ? "+" : "-"}
                                        {currSymbol}
                                        {getFormattedAmount(
                                            Math.abs(plValue),
                                            true,
                                            2
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const portfolioNetPl =
        itemData.netPl !== undefined
            ? Number(itemData.netPl) || 0
            : (Number(itemData.currentValue) || 0) -
              (Number(itemData.totalInvestment) || 0);
    let portfolioNetPlColor = Tokens.textSecondary;
    if (portfolioNetPl > 0) portfolioNetPlColor = positiveColor;
    if (portfolioNetPl < 0) portfolioNetPlColor = negativeColor;

    let secondaryStatValue = "—";
    if (itemData.netPl !== undefined) {
        const sign = portfolioNetPl >= 0 ? "+" : "-";
        secondaryStatValue = `${sign}${currSymbol} ${getFormattedAmount(
            Math.abs(portfolioNetPl)
        )}`;
    } else if (itemData.avgPrice) {
        secondaryStatValue = `${currSymbol} ${getFormattedAmount(
            itemData.avgPrice
        )}`;
    }

    return (
        <div key={itemInfo.uid} style={{ marginBottom: "12px" }}>
            <div
                role="button"
                tabIndex={0}
                onClick={handleCardClick}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardClick();
                    }
                }}
                style={{
                    padding: "20px",
                    borderRadius: Tokens.radiusCard,
                    border: `1px solid ${Tokens.hairline}`,
                    backgroundColor: Tokens.card,
                    cursor: "pointer",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                    }}
                >
                    <div>
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "16px",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            {itemInfo.name}
                        </Title>
                        {itemData.assetSummary && (
                            <div
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "12px",
                                    fontFamily: Tokens.fontMono,
                                    marginTop: "2px",
                                }}
                            >
                                {itemData.assetSummary}
                            </div>
                        )}
                    </div>
                    <Button
                        style={{
                            paddingRight: 0,
                            fontWeight: 500,
                            color: Tokens.textSecondary,
                            fontSize: "13px",
                        }}
                        type="link"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick();
                        }}
                        tabIndex="0"
                    >
                        View <ArrowRightOutlined />
                    </Button>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "12px",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Current value
                        </div>
                        <Title
                            level={4}
                            style={{
                                margin: "4px 0 0",
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "18px",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {currSymbol}
                            &nbsp;
                            {getFormattedAmount(itemData.currentValue)}
                        </Title>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-end",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Profit / loss
                        </div>
                        <Title
                            level={4}
                            style={{
                                margin: "4px 0 0",
                                color: plColor,
                                fontWeight: 500,
                                fontSize: "18px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {getFormattedPercentage(itemData.pl)}
                        </Title>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            Invested amount
                        </div>
                        <Title
                            level={5}
                            style={{
                                margin: "4px 0 0",
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "15px",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {currSymbol}
                            &nbsp;
                            {getFormattedAmount(itemData.totalInvestment)}
                        </Title>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-end",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                            }}
                        >
                            {itemData.netPl !== undefined
                                ? "Net P&L"
                                : "Avg price"}
                        </div>
                        <Title
                            level={5}
                            style={{
                                margin: "4px 0 0",
                                color:
                                    itemData.netPl !== undefined
                                        ? portfolioNetPlColor
                                        : Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "15px",
                                fontFamily:
                                    itemData.netPl !== undefined
                                        ? Tokens.fontMono
                                        : undefined,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {secondaryStatValue}
                        </Title>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverviewCard;
